import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { applicationsApi } from "./api";
import { Button, Card, PageTitle, StatusBadge } from "../../components/ui";
import { QueryView } from "../../components/states";
import { formatDateRange } from "../../lib/datetime";

export function SchoolApplicationsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["applications", "school"],
    queryFn: () => applicationsApi.list(),
  });
  // 振り分け支援（F-08・最小）: 自校の合計受入人数を状態別に見せる。取得失敗しても一覧は出す。
  const summaryQuery = useQuery({
    queryKey: ["applications", "summary"],
    queryFn: applicationsApi.summary,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => applicationsApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });

  const renotify = useMutation({
    mutationFn: (id: string) => applicationsApi.renotify(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });

  return (
    <div className="space-y-6">
      <PageTitle title="申込一覧" subtitle="自校からの申込状況を確認できます。" />

      {summaryQuery.isError && (
        <Card>
          <p className="text-xs font-bold text-slate-400">
            受入枠サマリーを取得できませんでした（申込一覧は下に表示されます）。
          </p>
        </Card>
      )}
      {summaryQuery.data && (
        <Card>
          <h3 className="mb-2 text-sm font-bold text-slate-500">受入枠サマリー（自校）</h3>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-2xl font-extrabold text-emerald-500">
                {summaryQuery.data.approved.headcount}
                <span className="ml-0.5 text-sm font-bold text-slate-400">人</span>
              </p>
              <p className="text-xs font-bold text-slate-500">
                確保済み（承認 {summaryQuery.data.approved.count} 社）
              </p>
            </div>
            <div>
              <p className="text-2xl font-extrabold text-brand">
                {summaryQuery.data.applied.headcount}
                <span className="ml-0.5 text-sm font-bold text-slate-400">人</span>
              </p>
              <p className="text-xs font-bold text-slate-500">
                承認待ち（申込中 {summaryQuery.data.applied.count} 社）
              </p>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            「確保済み」は企業が承認した受入人数の合計です。不足していれば「企業を探す」から追加で申し込めます。
          </p>
        </Card>
      )}

      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="まだ申込がありません。「企業を探す」から申し込めます。"
      >
        {(data) => (
          <div className="space-y-3">
            {data.items.map((a) => (
              <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-extrabold text-brand-dark">{a.companyName}</h3>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {formatDateRange(a.startDate, a.endDate)}・{a.headcount}人・{a.desiredJobType}
                  </p>
                  {a.status === "rejected" && a.rejectReason && (
                    <p className="mt-1 text-xs text-rose-500">却下理由: {a.rejectReason}</p>
                  )}
                  {a.notifiedPending && (
                    <p className="mt-1 text-xs font-bold text-amber-600">
                      ⚠
                      企業へメール通知できていない可能性があります。電話で確認するか、下の「再送」をお試しください。
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {a.notifiedPending && (
                    <Button
                      variant="ghost"
                      onClick={() => renotify.mutate(a.id)}
                      disabled={renotify.isPending}
                    >
                      通知を再送
                    </Button>
                  )}
                  {(a.status === "applied" || a.status === "approved") && (
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (confirm("この申込を取り消しますか？")) cancel.mutate(a.id);
                      }}
                      disabled={cancel.isPending}
                    >
                      取り消す
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </QueryView>
    </div>
  );
}
